import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAssignFunction,
  useFunction,
  usePeople,
  useUnassignFunction,
  useUpdateFunction,
} from '../../../platform/api/hooks';
import { asList, asRecord, str } from '../../../platform/api/utils';
import { labelOf } from '../../../shared/labels';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

const TYPE_LABELS: Record<string, string> = {
  RELIGIOUS: 'Religioso',
  ADMINISTRATIVE: 'Administrativo',
  OPERATIONAL: 'Operacional',
  SUPPORT: 'Apoio',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativo',
  ENDED: 'Encerrado',
  SUSPENDED: 'Suspenso',
  INACTIVE: 'Inativo',
  ARCHIVED: 'Arquivado',
};

function displayName(p: Record<string, unknown>): string {
  return str(p.socialName) || str(p.civilName) || '—';
}

export function FunctionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = useFunction(id ?? '');
  const updateFn = useUpdateFunction();
  const assignFn = useAssignFunction();
  const unassignFn = useUnassignFunction();
  const { data: peopleData } = usePeople({ limit: 100, status: 'ACTIVE' });
  const people = asList(peopleData);

  const [personId, setPersonId] = useState('');
  const [showAssign, setShowAssign] = useState(false);

  if (isLoading) return <div style={{ padding: '2rem' }}>Carregando...</div>;
  if (isError || !id) {
    return (
      <div style={{ padding: '2.25rem 2.5rem' }}>
        <Section title="Erro">
          <p style={{ color: 'var(--danger)' }}>Não foi possível carregar o cargo.</p>
        </Section>
      </div>
    );
  }

  const fn = asRecord(data);
  const assignments = asList(fn.assignments);

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <button
        type="button"
        onClick={() => {
          void navigate('/cargos');
        }}
        style={linkBtn}
      >
        ← Voltar para cargos e funções
      </button>

      <div style={{ marginTop: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{str(fn.name)}</h2>
          <Badge label={labelOf(TYPE_LABELS, fn.type)} variant="info" />
          <Badge
            label={labelOf(STATUS_LABELS, fn.status)}
            variant={str(fn.status) === 'ACTIVE' ? 'success' : 'default'}
          />
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '0.35rem 0 0' }}>
          {str(fn.description, 'Sem descrição')}
        </p>
        <p style={{ color: 'var(--warning)', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>
          Este cargo é institucional e não libera permissões digitais no sistema.
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <Section
          title="Pessoas com este cargo"
          action={
            <button
              type="button"
              onClick={() => {
                setShowAssign(true);
              }}
              style={smallBtn}
            >
              + Atribuir pessoa
            </button>
          }
        >
          {showAssign && (
            <div
              style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}
            >
              <select
                value={personId}
                onChange={(e) => {
                  setPersonId(e.target.value);
                }}
                style={{ ...inputCss, flex: 1, minWidth: 200 }}
              >
                <option value="">Selecione uma pessoa...</option>
                {people.map((raw) => {
                  const p = asRecord(raw);
                  return (
                    <option key={str(p.id)} value={str(p.id)}>
                      {displayName(p)}
                    </option>
                  );
                })}
              </select>
              <button
                type="button"
                disabled={!personId || assignFn.isPending}
                onClick={() => {
                  assignFn.mutate({ id, personId });
                  setPersonId('');
                  setShowAssign(false);
                }}
                style={primaryBtn}
              >
                Atribuir
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAssign(false);
                }}
                style={secondaryBtn}
              >
                Cancelar
              </button>
            </div>
          )}

          {assignments.length === 0 ? (
            <p style={{ color: 'var(--faint)', fontSize: '0.875rem' }}>Nenhuma pessoa atribuída.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {assignments.map((raw) => {
                const a = asRecord(raw);
                const person = asRecord(a.person);
                return (
                  <div
                    key={str(a.id)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.5rem 0.75rem',
                      background: 'var(--paper)',
                      borderRadius: 10,
                      border: '1px solid var(--line-soft)',
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>
                        {displayName(person)}
                      </span>
                      <div style={{ marginTop: 4 }}>
                        <Badge
                          label={labelOf(STATUS_LABELS, a.status)}
                          variant={str(a.status) === 'ACTIVE' ? 'success' : 'default'}
                        />
                      </div>
                    </div>
                    {str(a.status) === 'ACTIVE' && (
                      <button
                        type="button"
                        onClick={() => {
                          unassignFn.mutate({ id, assignmentId: str(a.id) });
                        }}
                        disabled={unassignFn.isPending}
                        style={{
                          ...smallBtn,
                          color: 'var(--danger)',
                          borderColor: 'var(--danger-soft)',
                        }}
                      >
                        Encerrar
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        {str(fn.status) === 'ACTIVE' && (
          <Section title="Situação do cargo">
            <button
              type="button"
              onClick={() => {
                updateFn.mutate({ id, data: { status: 'INACTIVE' } });
              }}
              style={secondaryBtn}
            >
              Desativar cargo
            </button>
          </Section>
        )}
      </div>
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--muted)',
  cursor: 'pointer',
  fontSize: '0.85rem',
  padding: 0,
};
const inputCss: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.875rem',
  outline: 'none',
};
const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.5rem 1rem',
  fontSize: '0.85rem',
  cursor: 'pointer',
  fontWeight: 600,
};
const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.5rem 1rem',
  fontSize: '0.85rem',
  cursor: 'pointer',
};
const smallBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--green)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.25rem 0.5rem',
  fontSize: '0.75rem',
  cursor: 'pointer',
};
